import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../../../utils/shadcn-ui";
import { useFormFieldId } from "../form/form-field-id-context";
import { BaseInput } from "./base-input";
import styles from "./number-input.module.css";
import { FieldProps, useField, useModelBinding } from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object> extends FieldProps<M, number | undefined> {
  width?: number;
  required?: boolean;
  disabled?: boolean;
  autofocus?: boolean;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
}

export const NumberInput = observer(function NumberInput<M extends object>({
  width,
  required,
  disabled,
  autofocus,
  className,
  min,
  max,
  step,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const elementRef = useRef<HTMLInputElement>(null);
  const { showErrors, tooltip } = useField(elementRef);

  // Default width for number inputs
  const effectiveWidth = useMemo(() => width ?? 80, [width]);

  const { value, onChange: onChangeValue } = useModelBinding<
    M,
    number | undefined
  >(restProps);

  const [strValue, setStrValue] = useState(valueToStrValue(value));

  const onChange = (newStrValue: string) => {
    setStrValue(newStrValue);
    onChangeValue(parseStrValue(newStrValue));
  };

  useEffect(() => {
    // Only sync from external value changes, not when the parsed local value matches
    const parsedLocal = parseStrValue(strValue);
    if (parsedLocal !== value) {
      setStrValue(valueToStrValue(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when external value changes, not on local typing
  }, [value]);

  return (
    <ValidationTooltip model={tooltip}>
      <BaseInput
        id={formFieldId}
        type="number"
        value={strValue}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        style={{ width: `${effectiveWidth}px`, maxWidth: "100%" }}
        required={required}
        ref={elementRef}
        className={cn(className, styles.root, showErrors && styles.showErrors)}
        disabled={disabled}
        autoFocus={autofocus}
        step={step ?? "any"}
        min={min}
        max={max}
      />
    </ValidationTooltip>
  );
});

function parseStrValue(strValue: string) {
  const num = parseFloat(strValue);
  return isNaN(num) ? undefined : num;
}

function valueToStrValue(value: number | undefined) {
  return value != null ? String(value) : "";
}
